/**
 * Asks how to reach the employer before a voice-agent call: schedule the
 * automated call sequence (the default), or place one call now.
 *
 * Scheduling keeps the verification automated and holds the calls to the
 * employer's calling hours, so a driver hired after hours still gets called
 * first thing the next business morning. "Call once now" is the one-off call
 * the button always placed.
 *
 * The modal only opens when the company has automated calling on and no call
 * is already scheduled; otherwise, or if the check fails, the one-off call
 * proceeds as before.
 */
import { useRef, useState } from 'react'

import { useApolloClient, useMutation } from '@apollo/client'
import { datadogRum } from '@datadog/browser-rum'
import { List, Modal, Text } from '@mantine/core'
import type {
  VoECallAutomationBlockedReason,
  VoECallAutomationPreview,
  VoEOutreachBlockedReason,
} from 'types/graphql'

import DNButton from 'src/components/Buttons/Button/DNButton'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  START_VOE_CALL_AUTOMATION,
  VOE_CALL_AUTOMATION_PREVIEW,
} from 'src/graphql/voe.graphql'
import { formatDateCustom } from 'src/lib/formatters'

type CallAutomationPreview = Pick<
  VoECallAutomationPreview,
  | 'state'
  | 'blockedReason'
  | 'overrides'
  | 'windowMonths'
  | 'attempts'
  | 'firstCallAt'
  | 'callingWindow'
>

export type AgentCallChoiceOutcome = 'scheduled' | 'call_now' | 'cancelled'

const DATE_TIME_FORMAT = 'ddd MM/DD h:mm A'

const BLOCKED_COPY: Record<VoECallAutomationBlockedReason, string> = {
  CLOSED: 'This verification is closed, so automated calls are off.',
  DO_NOT_CONTACT: 'The applicant asked us not to contact this employer.',
  NOT_A_CARRIER:
    "This employer isn't a carrier, so automation doesn't call it.",
  PLATFORM_ONLY: 'This carrier only verifies through its platform.',
  HAS_EMAIL: 'This employer has an email, so automated outreach emails them.',
  HAS_PLATFORM: 'This employer verifies through a platform.',
  NO_PHONE: 'There is no phone number to call.',
  EXHAUSTED: 'Every automated call to this number has already been made.',
}

const overrideCopy = (
  reason: VoEOutreachBlockedReason,
  windowMonths?: number | null
) => {
  switch (reason) {
    case 'DO_NOT_CONTACT':
      return 'Scheduling approves automated calls to this employer.'
    case 'CURRENT_EMPLOYER':
      return "This is the applicant's current employer. Scheduling approves automated calls to them."
    case 'OUT_OF_WINDOW':
      return `This employment is outside your ${
        windowMonths ? `${windowMonths}-month ` : ''
      }verification window. Scheduling calls them anyway.`
    case 'AWAITING_MANUAL':
      return 'This starts automated calls before your required manual attempts.'
    default:
      return null
  }
}

/** "right away", or when calling hours next open, in the recruiter's time. */
export const describeFirstCall = (
  firstCallAt: string | Date | null | undefined,
  now = new Date()
) => {
  if (!firstCallAt) return 'right away'
  const at = new Date(firstCallAt)
  return at.getTime() - now.getTime() <= 60 * 1000
    ? 'right away'
    : formatDateCustom(at, DATE_TIME_FORMAT)
}

export const describeSchedule = (preview: CallAutomationPreview) => {
  const calls =
    preview.attempts === 1 ? '1 call' : `up to ${preview.attempts} calls`
  const window = preview.callingWindow
    ? ` during the employer's calling hours (${preview.callingWindow})`
    : " during the employer's calling hours"
  return `The voice agent places ${calls}${window}. The first call goes out ${describeFirstCall(
    preview.firstCallAt
  )}.`
}

interface Pending {
  voeId: string
  preview: CallAutomationPreview
  callNow: () => void
}

export const useAgentCallChoice = () => {
  const client = useApolloClient()
  const [pending, setPending] = useState<Pending | null>(null)
  // Kept after close so the copy does not blank during the exit animation.
  const [shown, setShown] = useState<Pending | null>(null)
  const checkingRef = useRef(false)

  const [startCallAutomation, { loading: scheduling }] = useMutation(
    START_VOE_CALL_AUTOMATION,
    { refetchQueries: 'active' }
  )

  const track = (
    name: 'voe_agent_call_choice_shown' | 'voe_agent_call_choice_decided',
    target: Pending,
    outcome?: AgentCallChoiceOutcome
  ) =>
    datadogRum.addAction(name, {
      surface: 'dashboard',
      voe_id: target.voeId,
      state: target.preview.state,
      blocked_reason: target.preview.blockedReason ?? null,
      overrides: target.preview.overrides,
      ...(outcome && { outcome }),
    })

  /**
   * Ask first when the call sequence could be scheduled; otherwise run
   * `callNow` straight away.
   */
  const chooseAgentCall = async (
    voeId: string,
    { callNow }: { callNow: () => void }
  ) => {
    // One check per click: a second click while it runs is ignored.
    if (checkingRef.current) return
    checkingRef.current = true

    let preview: CallAutomationPreview | null = null
    try {
      const { data } = await client.query({
        query: VOE_CALL_AUTOMATION_PREVIEW,
        variables: { voeId },
        fetchPolicy: 'network-only',
      })
      preview = data?.voeCallAutomationPreview ?? null
    } catch {
      // Never block the recruiter from calling because the check failed.
    } finally {
      checkingRef.current = false
    }

    // NONE: the company doesn't automate calls. RUNNING: a call is already
    // scheduled, which the scheduled-outreach prompt handles. CLOSED: the
    // verification is done, so there is nothing to schedule.
    if (
      !preview ||
      preview.state === 'NONE' ||
      preview.state === 'RUNNING' ||
      preview.blockedReason === 'CLOSED'
    ) {
      callNow()
      return
    }

    const target = { voeId, preview, callNow }
    setPending(target)
    setShown(target)
    track('voe_agent_call_choice_shown', target)
  }

  const decide = async (outcome: AgentCallChoiceOutcome) => {
    const target = pending
    if (!target || scheduling) return

    if (outcome === 'scheduled') {
      try {
        const { data } = await startCallAutomation({
          variables: { voeId: target.voeId },
        })
        track('voe_agent_call_choice_decided', target, outcome)
        setPending(null)
        toast(
          `Automated calls scheduled. The first call goes out ${describeFirstCall(
            data?.startVoECallAutomation?.firstCallAt
          )}.`,
          'success'
        )
      } catch (error) {
        // Stay open so the recruiter can call now instead.
        toast((error as Error).message, 'error')
      }
      return
    }

    track('voe_agent_call_choice_decided', target, outcome)
    setPending(null)
    if (outcome === 'call_now') target.callNow()
  }

  const preview = shown?.preview
  const canSchedule = preview?.state === 'READY'
  const notes =
    preview?.overrides
      .map((reason) => overrideCopy(reason, preview.windowMonths))
      .filter((note): note is string => Boolean(note)) ?? []

  const agentCallPrompt = (
    <Modal
      opened={pending != null}
      onClose={() => !scheduling && decide('cancelled')}
      title="Call the employer with the voice agent"
      centered
      size="md"
      zIndex={210}
      classNames={{ title: 'font-semibold' }}
      // The modal portals out, but React still bubbles its clicks through the
      // component tree — keep them from reaching a clickable row or card.
      onClick={(event: React.MouseEvent) => event.stopPropagation()}
    >
      <div className="flex flex-col gap-4">
        {preview && canSchedule && (
          <Text size="sm" className="text-doubleNickel-gray-700">
            {describeSchedule(preview)} You can reschedule or cancel the calls
            under Scheduled Actions.
          </Text>
        )}
        {preview && !canSchedule && preview.blockedReason && (
          <Text size="sm" className="text-doubleNickel-gray-700">
            {BLOCKED_COPY[preview.blockedReason]} You can still place one call
            now.
          </Text>
        )}
        {canSchedule && notes.length > 0 && (
          <List size="sm" className="text-doubleNickel-gray-700">
            {notes.map((note) => (
              <List.Item key={note}>{note}</List.Item>
            ))}
          </List>
        )}
        <div className="flex flex-row flex-wrap justify-end gap-3">
          <DNButton
            variant="transparent"
            disabled={scheduling}
            onClick={() => decide('cancelled')}
          >
            Cancel
          </DNButton>
          <DNButton
            variant="outline"
            disabled={scheduling}
            onClick={() => decide('call_now')}
          >
            Call once now
          </DNButton>
          <DNButton
            variant="primary"
            loading={scheduling}
            disabled={!canSchedule}
            onClick={() => decide('scheduled')}
          >
            Schedule calls
          </DNButton>
        </div>
      </div>
    </Modal>
  )

  return { chooseAgentCall, agentCallPrompt }
}
